import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StudentNav } from '../../../common/student-nav/student-nav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MeetingService } from '../../../../services/meeting.service';
import { RoleService } from '../../../../services/role.service';
import { UserService } from '../../../../services/user.service';
import { NetworkService } from '../../../../services/network';
import { Meeting, MeetingState } from '../../../../models/meeting';
import { injectFocusTarget } from '../../../../helpers/focus-target';
import { RequestMeetingDialogComponent, DialogFaculty } from './meeting-request/meeting-request';
import {
  MeetingDetailsDialogComponent,
  MeetingDetailsDialogResult,
} from './meeting-details/meeting-details';
import { MessagingWidget } from '../../../common/messaging-widget/messaging-widget';

/** The filter dropdown's states, plus All. Shared shape with the counselor view. */
export type MeetingFilter = 'all' | MeetingState;

/**
 * Student-facing meetings page. Loads the student's meetings once and derives
 * the Upcoming, Pending, and Previous tab views from that single list.
 */
@Component({
  selector: 'app-meetings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    StudentNav,
    MatTabsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MessagingWidget,
  ],
  templateUrl: './meetings.html',
  styleUrl: './meetings.css',
})
export class Meetings implements OnInit {
  meetings: Meeting[] = [];
  counselors: DialogFaculty[] = [];
  meetingFilter: MeetingFilter = 'upcoming';
  searchQuery = '';

  /**
   * Mirrors the counselor's bookings filter.
   * Pending is an option here rather than a tab, since students have no tabs.
   */
  readonly filters: { value: MeetingFilter; label: string }[] = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'pending', label: 'Pending' },
    { value: 'previous', label: 'Previous' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'denied', label: 'Denied' },
    { value: 'all', label: 'All' },
  ];
  meetingService = inject(MeetingService);
  userService = inject(UserService);
  networkService = inject(NetworkService);
  roleService = inject(RoleService);
  cdf = inject(ChangeDetectorRef);
  router = inject(Router);
  route = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  /**
   * Opens the meeting a notification pointed at, on a filter that shows it so the
   * dialog isn't sitting over a list that excludes it.
   */
  private focusTarget = injectFocusTarget<Meeting>({
    param: 'meetingId',
    items: () => this.meetings,
    onFocus: (meeting) => {
      this.meetingFilter = meeting.state === 'expired' ? 'previous' : meeting.state;
      this.cdf.detectChanges();
      this.openDetailsDialog(meeting);
    },
  });

  /**
   * Loads the student's meetings and their assigned counselor on initialization.
   * @returns {void}
   */
  ngOnInit() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const q = params.get('q') ?? '';
      if (q !== this.searchQuery) {
        this.searchQuery = q;
        this.cdf.detectChanges();
      }
    });

    this.getMeetings();
    this.getCounselors();

    this.meetingService.meetingsChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getMeetings());
  }

  /**
   * Updates the search box and mirrors it into the URL's `q` param, so the
   * global search results page can hand off here with a query and this page
   * stays linkable/bookmarkable while typing.
   * @param {string} value - The new search box value.
   * @returns {void}
   */
  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: value || null },
      replaceUrl: true,
    });
  }

  /**
   * Loads the student's meetings from the API into `meetings`. The manual
   * detectChanges() call refreshes the view once the async response arrives,
   * following the same pattern as the Careers page.
   * @returns {void}
   */
  getMeetings() {
    this.meetingService.getMeetings().subscribe({
      next: (meetings) => {
        this.meetings = meetings;
        this.cdf.detectChanges();
        this.focusTarget.check();
      },
    });
  }

  /**
   * Loads the logged-in student's faculty network
   * request dialog can book with a real counselor id and name.
   * @returns {void}
   */
  getCounselors() {
    this.meetingService.getAvailableFaculty().subscribe({
      next: (faculty) => {
        this.counselors = faculty.map((f) => ({
          id: f.id,
          name: f.preferredName ?? `${f.first_name} ${f.last_name}`,
          userId: f.id,
        }));
      },
      error: () => {
        this.counselors = [];
      },
    });
  }

  getOtherUser(connection: any): any {
    const userId = this.userService.currentUser?.id;
    return connection.requester_id === userId ? connection.addressee : connection.requester;
  }

  /**
   * Opens the request-meeting dialog and re-fetches once the student submits.
   * @returns {void}
   */
  openRequestMeetingDialog(): void {
    this.dialog
      .open(RequestMeetingDialogComponent, {
        data: { counselors: this.counselors, existingMeetings: this.meetings },
        width: '900px',
        maxWidth: '96vw',
        // Title + content + actions have to fit the viewport; the content's own
        // cap (see meeting-request.css) does the rest.
        maxHeight: '92vh',
      })
      .afterClosed()
      .subscribe((created?: Meeting) => {
        if (created) {
          this.getMeetings();
        }
      });
  }

  openDetailsDialog(meeting: Meeting): void {
    this.dialog
      .open(MeetingDetailsDialogComponent, {
        data: { meeting },
        width: '980px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: MeetingDetailsDialogResult) => {
        if (!result) return;
        if (result.action === 'canceled') {
          // Reflect the cancellation locally so the card moves to Previous
          // without a re-fetch.
          const target = this.meetings.find((m) => m.id === meeting.id);
          if (target) target.status = 'canceled';
          this.meetings = [...this.meetings];
          this.cdf.detectChanges();
        } else if (result.action === 'responded') {
          // Re-fetch to reflect the saved state: proposing, accepting, or
          // declining a change all update fields the list needs fresh.
          this.getMeetings();
        }
      });
  }

  /**
   * Confirmed meetings whose start time is still in the future.
   * @returns {Meeting[]}
   */
  get filteredMeetings(): Meeting[] {
    return this.applySearch(this.meetingsByStatus);
  }

  private get meetingsByStatus(): Meeting[] {
    switch (this.meetingFilter) {
      case 'upcoming':
        return this.upcomingMeetings;
      case 'pending':
        return this.pendingMeetings;
      case 'previous':
        return this.previousMeetings;
      case 'canceled':
      case 'denied':
        return this.meetings.filter((m) => m.state === this.meetingFilter);
      default: {
        const now = Date.now();
        return [...this.meetings].sort((a, b) => {
          const aTime = a.start?.getTime() ?? 0;
          const bTime = b.start?.getTime() ?? 0;
          const aFuture = aTime >= now;
          const bFuture = bTime >= now;
          if (aFuture && !bFuture) return -1;
          if (!aFuture && bFuture) return 1;
          return aFuture ? aTime - bTime : bTime - aTime;
        });
      }
    }
  }

  /** Narrows a status bucket down to meetings matching the search box, by
   *  subject, counselor name, or location. Empty query is a no-op. */
  private applySearch(meetings: Meeting[]): Meeting[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return meetings;
    return meetings.filter((m) =>
      [m.title, m.facultyName, m.location].some((field) => field?.toLowerCase().includes(q)),
    );
  }

  /** Empty-state copy phrased for whichever filter is active. */
  get emptyMessage(): string {
    switch (this.meetingFilter) {
      case 'upcoming':
        return 'You have no upcoming meetings';
      case 'pending':
        return 'You have no requests awaiting a response';
      case 'previous':
        return 'You have no previous meetings';
      case 'canceled':
        return 'No meetings have been canceled';
      case 'denied':
        return 'None of your requests have been denied';
      default:
        return 'You have no meetings yet';
    }
  }

  isUpcoming(m: Meeting): boolean {
    return m.state === 'upcoming';
  }

  isPending(m: Meeting): boolean {
    return m.state === 'pending';
  }

  /** Whether this meeting has a proposal awaiting THIS student's own
   *  response. Drives the small red "needs attention" dot on its card. */
  needsAttention(m: Meeting): boolean {
    return m.awaitingResponseUserId === this.userService.currentUser?.id;
  }

  get upcomingMeetings(): Meeting[] {
    return this.meetings.filter((m) => m.state === 'upcoming');
  }

  /**
   * Requests a counselor can still answer. A lapsed one belongs under Previous,
   * not in a bucket implying a response is coming.
   * @returns {Meeting[]}
   */
  get pendingMeetings(): Meeting[] {
    return this.meetings.filter((m) => m.state === 'pending');
  }

  /**
   * Meetings the clock has moved past: ones that happened, plus requests that
   * lapsed unanswered. Canceled and denied have their own filters and are not
   * included here. Lapsed requests ride along instead of getting a sixth option
   * as 'Expired'.
   * @returns {Meeting[]}
   */
  get previousMeetings(): Meeting[] {
    return this.meetings.filter((m) => m.state === 'previous' || m.state === 'expired');
  }
}
